import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header } from '../../components/ui/Header';
import UserCard from './components/UserCard';
import AddUserModal from './components/AddUserModal';
import EditUserModal from './components/EditUserModal';
import LoadingSpinner from '../login-authentication/components/LoadingSpinner';
import userManagementService from '../../services/userManagementService';
import { Users, Plus, Shield, Activity } from 'lucide-react';

const UserManagementDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  const { userProfile, signOut } = useAuth();
  const navigate = useNavigate();

  // Check admin access
  useEffect(() => {
    if (userProfile && userProfile?.role !== 'admin') {
      navigate('/product-order-management');
      return;
    }
  }, [userProfile, navigate]);

  // Load users
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await userManagementService?.getAllUsers();
      
      if (result?.success) {
        setUsers(result?.users || []);
      } else {
        setError(result?.error || 'Errore nel caricare gli utenti');
      }
    } catch (error) {
      setError('Errore di connessione nel caricare gli utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    setActionLoading(true);
    try {
      const result = currentStatus 
        ? await userManagementService?.deactivateUser(userId)
        : await userManagementService?.activateUser(userId);
      
      if (result?.success) {
        await loadUsers(); // Reload users
      } else {
        setError(result?.error || 'Errore nell\'aggiornare lo stato utente');
      }
    } catch (error) {
      setError('Errore di connessione nell\'aggiornare l\'utente');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
  };

  const handleDeleteUser = async (user) => {
    if (!user?.id) {
      return;
    }

    if (user?.id === userProfile?.id) {
      setError('Non puoi eliminare l\'utente con cui sei autenticato');
      return;
    }

    const confirmed = window.confirm(`Vuoi davvero eliminare l'utente "${user?.full_name}"?`);
    if (!confirmed) {
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const result = await userManagementService.deleteUser(user.id);
      if (!result?.success) {
        setError(result?.error || 'Errore nell\'eliminazione dell\'utente');
        return;
      }

      await loadUsers();
    } catch (deleteError) {
      setError('Errore di connessione nell\'eliminare l\'utente');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen overflow-x-clip bg-gray-50">
      {/* Header */}
      <Header 
        title="Gestione Utenti"
        subtitle="Pannello amministrativo per la gestione degli utenti del sistema"
        userProfile={userProfile}
        onLogout={handleLogout}
        actions={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <button
              onClick={() => navigate('/statistics-dashboard')}
              className="flex w-full items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto"
            >
              <Activity className="w-4 h-4 mr-2" />
              Statistiche
            </button>
            <button
              onClick={() => navigate('/product-order-management')}
              className="flex w-full items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 sm:w-auto"
            >
              Ordini
            </button>
          </div>
        }
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Utenti Totali</p>
                <p className="text-2xl font-semibold text-gray-900">{users?.length || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Shield className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Utenti Attivi</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {users?.filter(u => u?.is_active)?.length || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Amministratori</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {users?.filter(u => u?.role === 'admin')?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Users Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-4 border-b border-gray-200 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-medium text-gray-900">Utenti Sistema</h2>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex w-full items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi Utente
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="px-6 py-4 bg-red-50 border-l-4 border-red-400">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Users Grid */}
          <div className="p-6">
            {users?.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun utente</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Inizia aggiungendo il primo utente al sistema.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {users?.map((user) => (
                  <UserCard
                    key={user?.id}
                    user={user}
                    onToggleStatus={handleToggleUserStatus}
                    onEditUser={handleEditUser}
                    onDeleteUser={handleDeleteUser}
                    loading={actionLoading}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onUserAdded={() => {
            setShowAddModal(false);
            loadUsers();
          }}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onUserUpdated={() => {
            setEditingUser(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

export default UserManagementDashboard;
